import * as React from 'react'
import { AlertCircle as WarningCircleIcon } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { decodeRouteParam } from '@atbash-labs/plantview-links'
import { useGetPlantEntityQuery } from '@axilon-apps/domains-ontology-ui-state'
import { Empty } from '../../components/primitives/Empty'
import { plantSchedulesIndexHref } from '../../lib/plant-links'
import { GenericEntityPage } from './entity/GenericEntityPage'
import { registeredLabel } from './entity/entity-surface'

/**
 * `/plant/schedules/:nodeId` — ScheduleProfile or Holiday detail under the
 * Schedules tab. Reuses the generic entity grammar (typed props, provenance,
 * session changes) while keeping chrome on Schedules via breadcrumb + route.
 */
const ScheduleEntityDetailPage = (): React.ReactElement => {
  const { nodeId = '' } = useParams<{ nodeId: string }>()
  const decodedNodeId = decodeRouteParam(nodeId)

  const { data, isLoading, isError } = useGetPlantEntityQuery(decodedNodeId, {
    skip: !decodedNodeId
  })
  const label = data ? registeredLabel(data.labels) : null
  const isScheduleLabel = label === 'ScheduleProfile' || label === 'Holiday'
  const title =
    (typeof data?.entity.name === 'string' && data.entity.name) || decodedNodeId || 'Schedule'

  return (
    <div className="flex flex-1 flex-col gap-card">
      <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
        <Link to={plantSchedulesIndexHref()} className="hover:text-ink hover:underline">
          Schedules
        </Link>
        <span className="mx-1.5 text-ink-4">/</span>
        <span className="text-ink">{title}</span>
      </nav>

      {isLoading ? (
        <div className="rounded-xl border bg-card p-6 text-sm text-ink-3 shadow-sm">Loading…</div>
      ) : isError || !data ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="Couldn't load this schedule entity"
            hint="It may have been deleted, or the proxy is unreachable."
            icon={WarningCircleIcon}
          />
        </div>
      ) : !isScheduleLabel ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="Not a schedule or holiday"
            hint="This id isn't a ScheduleProfile or Holiday. Open it from Entity view instead."
            icon={WarningCircleIcon}
          />
        </div>
      ) : (
        <GenericEntityPage result={data} label={label} />
      )}
    </div>
  )
}

export default ScheduleEntityDetailPage
